/**
 * Logos pequeños junto a un número de teléfono: una marca por cada cuenta (Ooma, USCaller 1-4, ruta propia Commandix) por la que YA se ha
 * llamado a ese número. Sin logo = nunca se ha llamado. Variante A acordada con el Founder (3-nov-2026): van DESPUÉS del número y ANTES del
 * contador ×N, sin tope (todos los logos que haya), envolviendo sin romper el texto si no caben en una línea.
 *
 * Varias instancias montadas a la vez (una tabla de clientes, una nota larga...) se agrupan en UNA sola petición a
 * `/attempts/counters` (reutiliza `fetchCounters` de lib/notas.js) con una ventana de micro-lote corta, y el resultado se cachea un
 * momento para que abrir/cerrar la misma pantalla no repita la llamada.
 */
import { useEffect, useState } from "react";
import { fetchCounters } from "../lib/notas";
import LogoCuenta from "./notas/LogoCuenta";

const CACHE_TTL_MS = 20000;   // caché breve: igual se refresca sola a cada montaje nuevo pasado este tiempo

const cache = new Map();   // phone -> { at, item }
let pendingPhones = new Set();
let pendingResolvers = [];   // [{ phone, resolve }]
let scheduled = false;

// Micro-lote por MICROTAREA (no por temporizador): agrupa los montajes que piden logos en el mismo turno de render
// en una sola petición a /attempts/counters sin dejar temporizadores reales pendientes (uno por fila/nota es demasiado).
function scheduleFetch(phone) {
  return new Promise((resolve) => {
    pendingPhones.add(phone);
    pendingResolvers.push({ phone, resolve });
    if (scheduled) return;
    scheduled = true;
    Promise.resolve().then(async () => {
      const phones = Array.from(pendingPhones);
      const resolvers = pendingResolvers;
      pendingPhones = new Set();
      pendingResolvers = [];
      scheduled = false;
      let items = [];
      try { items = await fetchCounters(phones); } catch (e) { items = []; }
      const byPhone = new Map(items.map((it) => [it.phone, it]));
      const now = Date.now();
      resolvers.forEach(({ phone: p, resolve: r }) => {
        const item = byPhone.get(p) || null;
        cache.set(p, { at: now, item });
        r(item);
      });
    });
  });
}

/** Solo para pruebas: vacía la caché/lote entre casos. */
export function _resetLogosNumeroCache() {
  cache.clear();
  pendingPhones = new Set();
  pendingResolvers = [];
  scheduled = false;
}

const NOMBRE_APP = { ooma: "Ooma", uscaller: "USCaller", commandix: "Teléfono Commandix" };

function nombreCuenta(a) {
  if (a.name) return a.name;
  const base = NOMBRE_APP[a.app] || a.app;
  return a.app === "uscaller" && a.slot ? `${base} ${a.slot}` : base;
}

function fmtHace(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const segs = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (segs < 60) return "hace un momento";
  const min = Math.round(segs / 60);
  if (min < 60) return `hace ${min} min`;
  const horas = Math.round(min / 60);
  if (horas < 24) return `hace ${horas} h`;
  const dias = Math.round(horas / 24);
  return `hace ${dias} d`;
}

export function tooltipCuenta(a) {
  const llamadas = a.count === 1 ? "1 llamada" : `${a.count} llamadas`;
  const ultima = a.last_at ? ` · última ${fmtHace(a.last_at)}` : "";
  return `${nombreCuenta(a)} · ${llamadas}${ultima}`;
}

/** `phone`: ya normalizado (+1XXXXXXXXXX); sin eso no hay nada que buscar. `size` en px (14-16 es lo acordado; Commandix ~18). */
export default function LogosNumero({ phone, size = 15 }) {
  const [item, setItem] = useState(() => {
    const c = phone && cache.get(phone);
    return c && Date.now() - c.at < CACHE_TTL_MS ? c.item : undefined;
  });
  useEffect(() => {
    if (!phone) { setItem(null); return; }
    const c = cache.get(phone);
    if (c && Date.now() - c.at < CACHE_TTL_MS) { setItem(c.item); return; }
    let vivo = true;
    scheduleFetch(phone).then((it) => { if (vivo) setItem(it); });
    return () => { vivo = false; };
  }, [phone]);

  const cuentas = (item?.by_account || []).filter((a) => a && a.count > 0 && a.app);
  if (!phone || !cuentas.length) return null;
  return (
    <span className="nt-num-logos" data-testid="nt-num-logos">
      {cuentas.map((a, i) => (
        <LogoCuenta key={`${a.app}-${a.slot ?? ""}-${i}`} cuenta={a} size={a.app === "commandix" ? Math.max(size, 18) : size} title={tooltipCuenta(a)} />
      ))}
    </span>
  );
}
